import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Default now uses muted (secondary style) for less visual noise
        default:
          "border-transparent bg-muted text-muted-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive/10 text-destructive",
        outline: "border border-border text-foreground",
        // Status variants using CSS variables
        success:
          "border border-[hsl(var(--status-success)/0.2)] bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))]",
        warning:
          "border border-[hsl(var(--status-warning)/0.2)] bg-[hsl(var(--status-warning)/0.1)] text-[hsl(var(--status-warning))]",
        info: "border border-[hsl(var(--status-info)/0.2)] bg-[hsl(var(--status-info)/0.1)] text-[hsl(var(--status-info))]",
        review:
          "border border-[hsl(var(--status-review)/0.2)] bg-[hsl(var(--status-review)/0.12)] text-[hsl(var(--status-review))]",
        // Priority variants using CSS variables
        "priority-critical":
          "border border-[hsl(var(--status-critical)/0.2)] bg-[hsl(var(--status-critical)/0.1)] text-[hsl(var(--status-critical))]",
        "priority-high":
          "border border-[hsl(var(--status-high)/0.2)] bg-[hsl(var(--status-high)/0.1)] text-[hsl(var(--status-high))]",
        "priority-medium":
          "border border-[hsl(var(--status-medium)/0.2)] bg-[hsl(var(--status-medium)/0.1)] text-[hsl(var(--status-medium))]",
        "priority-low":
          "border border-[hsl(var(--status-low)/0.2)] bg-[hsl(var(--status-low)/0.1)] text-[hsl(var(--status-low))]",
        // Bid stage variants using CSS variables — 7-state lifecycle
        "stage-lead":
          "border border-[hsl(var(--stage-lead)/0.2)] bg-[hsl(var(--stage-lead)/0.1)] text-[hsl(var(--stage-lead))]",
        "stage-qualifying":
          "border border-[hsl(var(--stage-qualifying)/0.2)] bg-[hsl(var(--stage-qualifying)/0.1)] text-[hsl(var(--stage-qualifying))]",
        "stage-drafting":
          "border border-[hsl(var(--stage-drafting)/0.2)] bg-[hsl(var(--stage-drafting)/0.1)] text-[hsl(var(--stage-drafting))]",
        "stage-proposal":
          "border border-[hsl(var(--stage-proposal)/0.2)] bg-[hsl(var(--stage-proposal)/0.1)] text-[hsl(var(--stage-proposal))]",
        "stage-submitted":
          "border border-[hsl(var(--stage-submitted)/0.2)] bg-[hsl(var(--stage-submitted)/0.1)] text-[hsl(var(--stage-submitted))]",
        "stage-won":
          "border border-[hsl(var(--stage-won)/0.2)] bg-[hsl(var(--stage-won)/0.1)] text-[hsl(var(--stage-won))]",
        "stage-lost":
          "border border-[hsl(var(--stage-lost)/0.2)] bg-[hsl(var(--stage-lost)/0.1)] text-[hsl(var(--stage-lost))]",
        "stage-no_go":
          "border border-[hsl(var(--stage-no_go)/0.2)] bg-[hsl(var(--stage-no_go)/0.1)] text-[hsl(var(--stage-no_go))]",
        "stage-closed":
          "border border-[hsl(var(--stage-lost)/0.2)] bg-[hsl(var(--stage-lost)/0.08)] text-[hsl(var(--stage-lost))]",
      },
    },
    defaultVariants: {
      variant: "secondary",  // Changed from default to secondary for less visual noise
    },
  }
)

// Variants whose semantics are "workflow state" — these show a status dot by default.
const STATUS_VARIANTS = new Set<string>([
  'success', 'warning', 'info', 'review', 'destructive',
  'priority-critical', 'priority-high', 'priority-medium', 'priority-low',
  'stage-lead', 'stage-qualifying', 'stage-drafting', 'stage-proposal',
  'stage-submitted', 'stage-won', 'stage-lost', 'stage-no_go', 'stage-closed',
])

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /** Show a status dot. Defaults to true for status/priority/stage variants, false for neutral ones. Explicit undefined means "use the variant default". */
  dot?: boolean | undefined
}

// forwardRef so the badge can sit inside a Radix `asChild` slot (Tooltip /
// Popover triggers) without React warning about refs on a function component.
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, dot, children, ...props }, ref) => {
    const showDot = dot ?? STATUS_VARIANTS.has(variant ?? '')
    return (
      <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props}>
        {showDot && (
          <span
            data-testid="badge-dot"
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-current"
          />
        )}
        {children}
      </div>
    )
  }
)
Badge.displayName = "Badge"

export { Badge, badgeVariants }
